import BooleanRenderer from '../../components/grid/BooleanRenderer.vue'
import DateRenderer from '../../components/grid/DateRenderer.vue'
import ImageRenderer from '../../components/grid/ImageRenderer.vue'
import { getFileUrlFromBucket } from '../api/helpers/get-file-url'
import type { BooleanCellParams, DateCellParams, ImageCellParams } from './cell-params'

import type { ColDef } from 'ag-grid-community'
import type { Component } from 'vue'

const BUCKET_URL_CACHE_LIMIT = 500
const bucketUrlCache = new Map<string, string>()

const resolveBucketUrl = (value?: string | null): string => {
   if (!value) return ''

   const cached = bucketUrlCache.get(value)
   if (cached !== undefined) return cached

   const resolved = getFileUrlFromBucket(value)
   if (bucketUrlCache.size >= BUCKET_URL_CACHE_LIMIT) bucketUrlCache.clear()
   bucketUrlCache.set(value, resolved)

   return resolved
}

export const defineColumn = {
   boolean: <T>(field: keyof T & string): ColDef<T> => ({
      cellRenderer: BooleanRenderer,
      cellRendererParams: (params: { data: T }): BooleanCellParams => ({
         value: params.data[field] as boolean | null,
      }),
   }),

   date: <T>(field: keyof T & string, type: 'relative' | 'short' | 'full' = 'full'): ColDef<T> => ({
      cellRenderer: DateRenderer,
      cellRendererParams: (params: { data: T }): DateCellParams => ({
         date: params.data[field] as string | Date | null,
         type,
      }),
   }),

   image: <T>(
      field: keyof T & string,
      options?: { contain?: boolean; bucket?: boolean }
   ): ColDef<T> => ({
      cellRenderer: ImageRenderer,
      cellRendererParams: (params: { data: T }): ImageCellParams => ({
         url: options?.bucket
            ? resolveBucketUrl(params.data[field] as string)
            : (params.data[field] as string | null),
         contain: options?.contain,
      }),
   }),

   custom: <T, TParams>(component: Component, paramsFactory: (data: T) => TParams): ColDef<T> => ({
      cellRenderer: component,
      cellRendererParams: (params: { data: T }) => paramsFactory(params.data),
      cellDataType: false,
   }),
}

export type ColumnRenderers<T> = Partial<Record<keyof T, ColDef<T>>>

export const defineRenderers = <T>(renderers: ColumnRenderers<T>): ColumnRenderers<T> => renderers
